import {
  Accordion,
  ActionIcon,
  Box,
  Center,
  Group,
  Loader,
  NumberInput,
  Table,
  Text
} from "@mantine/core";
import { IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import { TopInventoryArticle } from "@app/services/drugDelivery/types";

interface TopInventoryViewProps {
  data: TopInventoryArticle[];
  isLoading: boolean;
  localQty: Record<string, number>;
  deletedLots: Set<string>;
  deliveryBy?: "insurance" | "freeSale" | "freeSaleWithOrder" | "ownInventory" | null;
  onIncrease: (key: string, maxStock: number) => void;
  onDecrease: (key: string) => void;
  onDelete: (key: string) => void;
  onSetQty: (key: string, value: number, maxStock: number) => void;
}

function formatDate(isoDate: string): string {
  const parts = (isoDate ?? "").split("T")[0].split("-");
  if (parts.length !== 3) {
    return isoDate;
  }
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

export default function TopInventoryView({
  data,
  isLoading,
  localQty,
  deletedLots,
  deliveryBy,
  onIncrease,
  onDecrease,
  onDelete,
  onSetQty
}: TopInventoryViewProps) {
  const isInsurance = deliveryBy === "insurance";

  if (isLoading) {
    return (
      <Center py="xl" data-testid="topinventory-loader">
        <Loader />
      </Center>
    );
  }

  if (!data.length) {
    return null;
  }

  return (
    <Box data-testid="topinventory-container">
      <Accordion data-testid="topinventory-accordion">
        {data.map((article) => {
          const visibleLots = article.lots.filter(
            (_, lotIndex) => !deletedLots.has(`${article.articleId}-${lotIndex}`)
          );

          return (
            <Accordion.Item
              key={article.articleId}
              value={String(article.articleId)}
              data-testid={`topinventory-product-${article.articleId}`}
            >
              <Accordion.Control>
                <Group justify="space-between" pr="sm">
                  <Box>
                    <Text fw={600} size="sm">
                      {article.articleName}
                    </Text>
                    {article.articleCategory && (
                      <Text size="xs" c="dimmed">
                        {article.articleCategory}
                      </Text>
                    )}
                  </Box>
                  <Text size="xs" c="#00b4cc" fw={500}>
                    {visibleLots.length} artículo(s) disponible(s)
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Table
                  className="biowel-table"
                  withTableBorder
                  data-testid={`topinventory-table-${article.articleId}`}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th bg="#fff">Lab</Table.Th>
                      <Table.Th bg="#fff">CUM</Table.Th>
                      <Table.Th bg="#fff">INVIMA</Table.Th>
                      <Table.Th bg="#fff">Lote</Table.Th>
                      <Table.Th bg="#fff" ta="center">
                        Vencimiento
                      </Table.Th>
                      <Table.Th bg="#fff" ta="center">
                        Stock disp.
                      </Table.Th>
                      {isInsurance && (
                        <Table.Th bg="#fff" ta="center">
                          Cant. ordenada
                        </Table.Th>
                      )}
                      <Table.Th bg="#fff" ta="center">
                        Cant. a entregar
                      </Table.Th>
                      <Table.Th bg="#fff" ta="center">
                        Acciones
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {visibleLots.length ? (
                      article.lots.map((lot, lotIndex) => {
                        const key = `${article.articleId}-${lotIndex}`;
                        if (deletedLots.has(key)) {
                          return null;
                        }

                        const qty = localQty[key] ?? 0;

                        return (
                          <Table.Tr
                            key={key}
                            data-testid={`topinventory-row-${article.articleId}-${lotIndex}`}
                          >
                            <Table.Td>{lot.lab ?? "-"}</Table.Td>
                            <Table.Td>{lot.cum ?? "-"}</Table.Td>
                            <Table.Td>{lot.invima ?? "-"}</Table.Td>
                            <Table.Td>{lot.lotName}</Table.Td>
                            <Table.Td ta="center">{formatDate(lot.expirationDate)}</Table.Td>
                            <Table.Td ta="center">{lot.availableStock}</Table.Td>
                            {isInsurance && (
                              <Table.Td ta="center">{lot.orderedQty ?? "-"}</Table.Td>
                            )}
                            <Table.Td ta="center">
                              <Box className="flex items-center justify-center gap-1">
                                <ActionIcon
                                  size="xs"
                                  variant="light"
                                  data-testid={`topinventory-decrement-${article.articleId}-${lotIndex}`}
                                  disabled={qty === 0}
                                  onClick={() => onDecrease(key)}
                                >
                                  <IconMinus size={12} />
                                </ActionIcon>
                                <NumberInput
                                  variant="unstyled"
                                  hideControls
                                  min={0}
                                  max={lot.availableStock}
                                  value={qty}
                                  w={40}
                                  styles={{ input: { textAlign: "center" } }}
                                  onChange={(value) =>
                                    onSetQty(
                                      key,
                                      typeof value === "number" ? value : 0,
                                      lot.availableStock
                                    )
                                  }
                                />
                                <ActionIcon
                                  size="xs"
                                  variant="light"
                                  data-testid={`topinventory-increment-${article.articleId}-${lotIndex}`}
                                  disabled={qty >= lot.availableStock}
                                  onClick={() => onIncrease(key, lot.availableStock)}
                                >
                                  <IconPlus size={12} />
                                </ActionIcon>
                              </Box>
                            </Table.Td>
                            <Table.Td ta="center">
                              <ActionIcon
                                color="red"
                                variant="light"
                                data-testid={`topinventory-delete-${article.articleId}-${lotIndex}`}
                                onClick={() => onDelete(key)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                    ) : (
                      <Table.Tr>
                        <Table.Td ta="center" colSpan={isInsurance ? 9 : 8}>
                          No hay lotes disponibles.
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Box>
  );
}
